import type { InputType, IVisibleLogic } from 'config'

export type DocumentGenerateLimitCategory = 'latePercentage' | 'gracePeriod' | 'defaultRate'

export interface IDocumentGenerateLimitRule {
  visibleLogic: IVisibleLogic[][]
  min?: string | number
  max?: string | number
}

export interface IDocumentGenerateDefaultsConfig {
  defaults: Record<string, any>
  rules: Record<DocumentGenerateLimitCategory, IDocumentGenerateLimitRule[]>
}

/**
 * Each category limits one Document Generate field. The min/max of the first
 * matching rule is enforced on that field for every loan.
 */
export const limitCategories: {
  key: DocumentGenerateLimitCategory
  title: string
  fieldKey: string
  fieldTitle: string
  minTitle: string
  maxTitle: string
  prefix?: string
  type: 'thousandSep' | 'number'
}[] = [
  {
    key: 'latePercentage',
    title: 'Late Percentage',
    fieldKey: 'latePercentage',
    fieldTitle: 'Late percentage',
    minTitle: 'Minimum Late Percentage',
    maxTitle: 'Maximum Late Percentage',
    prefix: '%',
    type: 'number',
  },
  {
    key: 'gracePeriod',
    title: 'Grace Period',
    fieldKey: 'gracePeriod',
    fieldTitle: 'Grace Period',
    minTitle: 'Minimum Grace Period',
    maxTitle: 'Maximum Grace Period',
    type: 'number',
  },
  {
    key: 'defaultRate',
    title: 'Default Rate',
    fieldKey: 'defaultRate',
    fieldTitle: 'Default Rate',
    minTitle: 'Minimum Default Rate',
    maxTitle: 'Maximum Default Rate',
    prefix: '%',
    type: 'number',
  },
]

export const emptyLimitRules = (): Record<DocumentGenerateLimitCategory, IDocumentGenerateLimitRule[]> => ({
  latePercentage: [],
  gracePeriod: [],
  defaultRate: [],
})

export const newLimitRule = (): IDocumentGenerateLimitRule => ({
  visibleLogic: [],
  min: '',
  max: '',
})

export const limitRuleInputsFor = (category: DocumentGenerateLimitCategory): Record<'min' | 'max', InputType> => {
  const meta = limitCategories.find((item) => item.key === category)!
  return {
    min: {
      inputType: 'text',
      type: meta.type,
      prefix: meta.prefix,
      title: meta.minTitle,
      required: false,
    },
    max: {
      inputType: 'text',
      type: meta.type,
      prefix: meta.prefix,
      title: meta.maxTitle,
      required: false,
    },
  }
}

/**
 * Default-value form for Document Generate configuration.
 * Mirrors the loan Document Generate "Details" fields that are sensible as
 * company-wide defaults (excludes loan-derived / disabled fields and dates).
 */
export const defaultInputs = (): Record<string, InputType> => {
  return {
    interestType: {
      title: 'Interest Type',
      inputType: 'select',
      hasDefaultOption: true,
      options: ['Dutch', 'Non Dutch'],
      required: false,
    },
    amortizationType: {
      title: 'Amortization Type',
      inputType: 'select',
      hasDefaultOption: true,
      options: ['ARM', 'Fixed', 'IO - ARM', 'IO - Fixed'],
      required: false,
    },
    escrowType: {
      title: 'Escrow Type',
      inputType: 'select',
      hasDefaultOption: true,
      options: ['Not Waived', 'Waive All', 'Waive Taxes', 'Waive Insurance', 'Waive Taxes and Insurance'],
      required: false,
    },
    latePercentage: {
      inputType: 'text',
      type: 'number',
      prefix: '%',
      title: 'Late percentage',
      required: false,
    },
    LateChargeMin: {
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'Minimum Late Charge',
      required: false,
    },
    gracePeriod: {
      inputType: 'text',
      type: 'number',
      title: 'Grace Period',
      required: false,
    },
    dailyLateCharge: {
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      title: 'Daily Late Charge',
      required: false,
    },
    dailyRateBasis: {
      inputType: 'select',
      title: 'Daily Rate Basis',
      options: {
        360: '30/360',
        365: '30/365',
        actual_360: 'Actual/360',
        actual_365: 'Actual/365',
      },
      hasDefaultOption: true,
      required: false,
    },
    paymentFrequency: {
      inputType: 'select',
      title: 'Payment Frequency',
      options: ['Annualy', 'Bimonthly', 'Biweekly', 'Daily', 'Monthly', 'Quarterly', 'Semi Annualy', 'Weekly'],
      hasDefaultOption: true,
      required: false,
    },
    acquisitionStatus: {
      inputType: 'select',
      title: 'Acquisition status',
      options: ['Property you own', 'New acquisition', 'Construction'],
      hasDefaultOption: true,
      required: false,
    },
    numberOfExtensions: {
      inputType: 'text',
      type: 'number',
      title: 'Number of Allowable Extensions',
      required: false,
    },
    extensionFeePerc: {
      inputType: 'text',
      type: 'text',
      title: 'Extension Fee Percent',
      value: '',
      prefix: '%',
      required: false,
    },
    titleAccountNumber: {
      inputType: 'text',
      type: 'text',
      title: 'Title Account Number',
      value: '',
      required: false,
    },
    IncrementOfExtensions: {
      inputType: 'text',
      type: 'number',
      title: 'Increment of Extensions',
      required: false,
    },
    personalGuaranty: {
      inputType: 'select',
      title: 'Personal Guaranty',
      options: ['Full Recourse', 'Non Recourse', 'Non Recourse with Bad Boy carveouts'],
      hasDefaultOption: true,
      required: false,
    },
    defaultRate: {
      inputType: 'text',
      type: 'number',
      prefix: '%',
      title: 'Default Rate',
      required: false,
    },
    maximumCombinedRate: {
      inputType: 'text',
      type: 'number',
      prefix: '%',
      title: 'Maximum Combined Rate',
      tooltip: 'Maximum allowed total of the loan interest rate plus the Document Generate default rate.',
      required: false,
    },
    enableExtension: {
      inputType: 'toggle',
      title: 'Enable Extension',
      value: false,
    },
  }
}

const migrateLegacyCombinedRule = (rule: any): Record<DocumentGenerateLimitCategory, IDocumentGenerateLimitRule[]> => {
  const result = emptyLimitRules()
  if (!rule || typeof rule !== 'object') return result
  const logic = Array.isArray(rule.visibleLogic) ? rule.visibleLogic : []

  if (rule.latePercentageMin !== undefined || rule.latePercentageMax !== undefined) {
    result.latePercentage.push({
      visibleLogic: cloneLogic(logic),
      min: rule.latePercentageMin ?? '',
      max: rule.latePercentageMax ?? '',
    })
  }
  if (rule.gracePeriodMin !== undefined || rule.gracePeriodMax !== undefined) {
    result.gracePeriod.push({
      visibleLogic: cloneLogic(logic),
      min: rule.gracePeriodMin ?? '',
      max: rule.gracePeriodMax ?? '',
    })
  }
  if (rule.defaultRateMin !== undefined || rule.defaultRateMax !== undefined) {
    result.defaultRate.push({
      visibleLogic: cloneLogic(logic),
      min: rule.defaultRateMin ?? '',
      max: rule.defaultRateMax ?? '',
    })
  }
  return result
}

const cloneLogic = (logic: IVisibleLogic[][]) => JSON.parse(JSON.stringify(logic || []))

/** Normalize legacy flat / combined-rule settings into { defaults, rules by category }. */
export const parseDocumentGenerateDefaults = (raw: any): IDocumentGenerateDefaultsConfig => {
  if (!raw || typeof raw !== 'object') return { defaults: {}, rules: emptyLimitRules() }

  if (raw.defaults || raw.rules) {
    const defaults = { ...(raw.defaults || {}) }
    // New shape: rules.latePercentage / gracePeriod / defaultRate
    if (raw.rules && !Array.isArray(raw.rules)) {
      return {
        defaults,
        rules: {
          // Rules saved under the retired `lateCharge` category are dropped: they
          // held dollar amounts and cannot be reused as percentage limits.
          latePercentage: Array.isArray(raw.rules.latePercentage) ? raw.rules.latePercentage : [],
          gracePeriod: Array.isArray(raw.rules.gracePeriod) ? raw.rules.gracePeriod : [],
          defaultRate: Array.isArray(raw.rules.defaultRate) ? raw.rules.defaultRate : [],
        },
      }
    }
    // Legacy shape: rules is a combined array
    if (Array.isArray(raw.rules)) {
      const rules = emptyLimitRules()
      raw.rules.forEach((rule: any) => {
        const migrated = migrateLegacyCombinedRule(rule)
        rules.latePercentage.push(...migrated.latePercentage)
        rules.gracePeriod.push(...migrated.gracePeriod)
        rules.defaultRate.push(...migrated.defaultRate)
      })
      return { defaults, rules }
    }
    return { defaults, rules: emptyLimitRules() }
  }

  return { defaults: { ...raw }, rules: emptyLimitRules() }
}
